/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH
 * @see PROJECT_LICENSE.txt
 */

import { v4 as uuidv4 } from 'uuid'

const LIVE_SEARCH_TIMEOUT_MS = 5000
const LIVE_SEARCH_MAX_RETRIES = 1
const LIVE_SEARCH_RETRY_DELAY_MS = 200
const GENDER_COOKIE_NAME = 'mey-gender'
const MALE_GENDER_COOKIE_VALUE = 'm'

const waitFor = (duration) => new Promise((resolve) => {
    setTimeout(resolve, duration)
})

/**
 * Build a dedicated out-of-range page error from GraphQL errors.
 *
 * @param {Array} errors
 * @returns {Error|null}
 */
const getOutOfBoundsPageError = (errors = []) => {
    if (!Array.isArray(errors) || errors.length === 0) {
        return null
    }

    const errorMessage = errors
        .map((error) => error?.extensions?.errorMessage || error?.message || '')
        .find((message) => message.includes('Page number is out of bounds'))

    if (!errorMessage) {
        return null
    }

    const maxValidPageMatch = errorMessage.match(/Maximum valid page is (\d+)/i)
    const maxValidPage = parseInt(maxValidPageMatch?.[1], 10)
    const error = new Error(errorMessage)

    error.isOutOfBoundsPageError = true
    error.maxValidPage = Number.isNaN(maxValidPage) ? 1 : maxValidPage

    return error
}

/**
 * Generates headers for Magento API requests
 */
const getHeaders = () => ({
    'Magento-Environment-Id': window.liveSearchConfig.environmentId,
    'Magento-Website-Code': window.liveSearchConfig.websiteCode,
    'Magento-Store-Code': window.liveSearchConfig.storeCode,
    'Magento-Store-View-Code': window.liveSearchConfig.storeViewCode,
    'X-Api-Key': window.liveSearchConfig.apiKey,
    'X-Request-Id': uuidv4(),
    'Content-Type': 'application/json'
    // "Magento-Customer-Group": config.customerGroup,
})

/**
 * Executes a product search via the Magento GraphQL API
 *
 * @param {string} url - API endpoint
 * @param {string} query - GraphQL query string
 * @param {Object} options - Variables for the query
 * @param {Function} errorHandler - Callback for error handling
 * @param retries
 * @returns {Promise<Object|undefined>} API response data or undefined on error
 */
const productSearch = async(url, query, options, errorHandler, retries = LIVE_SEARCH_MAX_RETRIES) => {
    let attempts = 0

    while (attempts <= retries) {
        try {
            const controller = new AbortController()
            const timeoutId = setTimeout(
                () => controller.abort(),
                LIVE_SEARCH_TIMEOUT_MS
            )

            const response = await fetch(url, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ query, variables: options }),
                signal: controller.signal
            })

            clearTimeout(timeoutId)

            if (!response.ok) {
                throw new Error(`Live Search responded with status ${response.status}.`)
            }

            const results = await response.json()

            if (results.errors?.length) {
                results.errors.forEach((err) => { console.error(err.message) })

                const outOfBoundsPageError = getOutOfBoundsPageError(results.errors)
                if (outOfBoundsPageError) {
                    throw outOfBoundsPageError
                }
            }

            if (!results?.data?.productSearch) {
                throw new Error('Live Search response is missing productSearch data.')
            }

            return results.data
        } catch (error) {
            attempts += 1

            if (attempts > retries) {
                if (error?.isOutOfBoundsPageError) {
                    throw error
                }

                if (typeof errorHandler === 'function') {
                    errorHandler(error)
                }

                throw error
            }

            console.warn(`Live Search request failed (attempt ${attempts} of ${retries + 1}). Retrying...`, error)
            await waitFor(LIVE_SEARCH_RETRY_DELAY_MS)
        }
    }
}

/**
 * Returns the gender filter label based on the selected state or cookie
 *
 * @returns {string} Gender filter label
 */
const getGenderLabels = () => window.liveSearchConfig?.departmentFilterLabels ?? {}

/**
 * Resolve current gender key based on Alpine store or fallback cookie
 *
 * @param {string|null} type
 * @returns {string}
 */
const getGenderKey = (type = null) => {
    const hasAlpine = typeof Alpine !== 'undefined' && typeof Alpine.store === 'function'
    const store = hasAlpine ? Alpine.store('genderToggle') : null

    if (store && (type === 'gender' || store.used)) {
        return store.checked ? 'men' : 'women'
    }

    return hyva.getCookie(GENDER_COOKIE_NAME) === MALE_GENDER_COOKIE_VALUE ? 'men' : 'women'
}

/**
 * Returns the gender filter label based on the selected state or cookie
 *
 * @param {string|null} type - Optional filter type
 * @param {string|null} overrideGenderKey - Optional gender key to force
 * @returns {string} Gender filter label
 */
const getGenderFilter = (type = null, overrideGenderKey = null) => {
    const labels = getGenderLabels()
    const genderKey = overrideGenderKey ?? getGenderKey(type)

    return labels[genderKey] ?? labels.men ?? labels.women ?? ''
}

/**
 * Retrieve gender label by key
 *
 * @param {string|null} genderKey
 * @returns {string}
 */
const getGenderLabelByKey = (genderKey = null) => {
    const labels = getGenderLabels()
    if (genderKey && labels[genderKey]) {
        return labels[genderKey]
    }

    const fallbackKey = getGenderKey()
    return labels[fallbackKey] ?? ''
}

/**
 * Get alternate gender key, falling back to available labels
 *
 * @param {string|null} genderKey
 * @returns {string}
 */
const getAlternateGenderKey = (genderKey = null) => {
    const labels = getGenderLabels()
    const baseKey = genderKey ?? getGenderKey()

    if (baseKey === 'men' && labels.women) {
        return 'women'
    }

    if (baseKey === 'women' && labels.men) {
        return 'men'
    }

    const availableKeys = Object.keys(labels)
    if (availableKeys.length > 0) {
        const fallbackKey = availableKeys.find(key => key !== baseKey)
        return fallbackKey ?? baseKey
    }

    return baseKey
}

/**
 * Check if a search response contains product items
 *
 * @param {Object|undefined} result
 * @returns {boolean}
 */
const hasProductResults = (result) => Array.isArray(result?.productSearch?.items) &&
    result.productSearch.items.length > 0

/**
 * Execute a search with gender-aware fallback logic
 *
 * @param {Function} executor - callback receiving the gender key and returning the search result
 * @returns {Promise<Object>}
 */
const executeGenderFallbackSearch = async(executor) => {
    const primaryGenderKey = getGenderKey()
    const primaryResponse = await executor(primaryGenderKey)
    const primaryHasResults = hasProductResults(primaryResponse)

    if (primaryHasResults) {
        return {
            response: primaryResponse,
            primaryResponse,
            fallbackResponse: null,
            primaryGenderKey,
            usedGenderKey: primaryGenderKey,
            fallbackGenderKey: null,
            fallbackTriggered: false,
            fallbackHasResults: false
        }
    }

    const fallbackGenderKey = getAlternateGenderKey(primaryGenderKey)
    if (fallbackGenderKey === primaryGenderKey) {
        return {
            response: primaryResponse,
            primaryResponse,
            fallbackResponse: null,
            primaryGenderKey,
            usedGenderKey: primaryGenderKey,
            fallbackGenderKey: null,
            fallbackTriggered: false,
            fallbackHasResults: false
        }
    }

    const fallbackResponse = await executor(fallbackGenderKey)
    const fallbackHasResults = hasProductResults(fallbackResponse)

    return {
        response: fallbackHasResults ? fallbackResponse : primaryResponse,
        primaryResponse,
        fallbackResponse,
        primaryGenderKey,
        usedGenderKey: fallbackHasResults ? fallbackGenderKey : primaryGenderKey,
        fallbackGenderKey,
        fallbackTriggered: true,
        fallbackHasResults
    }
}

export {
    productSearch,
    getHeaders,
    getGenderFilter,
    getGenderLabelByKey,
    getAlternateGenderKey,
    executeGenderFallbackSearch,
    getOutOfBoundsPageError
}
